'use client';

import { type PropsWithChildren, useMemo } from 'react';

import { DirectionProvider } from '@radix-ui/react-direction';
import { merge } from 'lodash';
import { NextIntlClientProvider, useLocale, useTimeZone } from 'next-intl';

import { useConfig } from '@/components/providers/config-provider';
import { ALL_MESSAGES } from '@/lib/constants/all-messages.generated';
import type { ClientSafeConfig } from '@/lib/types/tenant';
import { getTextDirection } from '@/lib/utils/locale-shared';

interface MessageLocaleWrapperProps extends PropsWithChildren {
  locale: string | undefined;
  /** When true, only provides locale context without adding a dir wrapper element. */
  inline?: boolean;
}

export function MessageLocaleWrapper({ locale, inline, children }: MessageLocaleWrapperProps) {
  const globalLocale = useLocale();
  const config = useConfig();

  if (!locale || locale === globalLocale) return <>{children}</>;

  const provider = (
    <MessageLocaleProvider locale={locale} translations={config.translations}>
      {children}
    </MessageLocaleProvider>
  );

  if (inline) return provider;

  const dir = getTextDirection(locale);
  return (
    <div dir={dir}>
      <DirectionProvider dir={dir}>{provider}</DirectionProvider>
    </div>
  );
}

function MessageLocaleProvider({
  locale,
  translations,
  children,
}: PropsWithChildren<{
  locale: string;
  translations?: ClientSafeConfig['translations'];
}>) {
  const timeZone = useTimeZone();
  const messages = useMemo(
    () =>
      merge(
        {} as Record<string, unknown>,
        ALL_MESSAGES['en'] || {},
        locale !== 'en' ? ALL_MESSAGES[locale] || {} : {},
        translations?.['en'] || {},
        locale !== 'en'
          ? (translations as Record<string, unknown> | undefined)?.[locale] || {}
          : {},
      ),
    [locale, translations],
  );

  return (
    <NextIntlClientProvider locale={locale} messages={messages} timeZone={timeZone}>
      {children}
    </NextIntlClientProvider>
  );
}
